import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../auth/useAuth.js'
import { useDataLoader } from '../useDataLoader.js'
import { useApi } from '../useApi.js'
import { quotesService } from '../../services/index.js'
import { useQuotePermissions } from './useQuotePermissions.js'
import { useQuoteCommercials } from './useQuoteCommercials.js'
import { useQuoteDetailAssign } from './useQuoteDetailAssign.js'
import { useQuoteDetailEdit } from './useQuoteDetailEdit.js'
import { isMissing } from '../../utils/quoteUtils.js'
import { quoteAttemptsService } from '../../services/index.js'

export function useQuoteDetail(quoteId) {
  const { user: currentUser } = useAuth()
  const router = useRouter()
  const quote = ref(null)
  const attempt = ref(null)
  
  const { loading, loadData } = useDataLoader({
    initialLoading: true,
    defaultErrorMessage: 'Erreur lors du chargement de la demande'
  })
  
  const { patch: apiPatch, delete: apiDelete } = useApi()

  // Permissions
  const { canEdit, canGenerateQuote, canChangeStatus } = useQuotePermissions(quote)

  // Commercials
  const { 
    commercials, 
    availableCommercials, 
    getCommercialsForSelect, 
    loadCommercials 
  } = useQuoteCommercials()

  // Edit mode
  const { 
    isEditing, 
    editedQuote, 
    saving, 
    startEditing, 
    cancelEditing, 
    saveQuote, 
    getValue 
  } = useQuoteDetailEdit(quoteId, quote, canEdit)

  const loadQuote = async (onLoadComplete) => {
    try {
      const data = await loadData(() => quotesService.detail(quoteId), {
        showErrorToast: false // Ne pas afficher le toast, on va rediriger
      })
      quote.value = data
      
      // Load associated attempt if quote has one
      // Note: We'll search for attempts linked to this quote
      // The attempt should have quoteId matching this quote's id
      if (data.id) {
        try {
          // Search for completed attempts with this quoteId
          const attemptsResponse = await quoteAttemptsService.list({ 
            limit: 100,
            completed: true
          })
          const associatedAttempt = attemptsResponse.attempts?.find(
            a => a.quoteId === data.id
          )
          if (associatedAttempt) {
            attempt.value = associatedAttempt
          }
        } catch (err) {
          // Silently fail - attempt loading is optional
          console.warn('Could not load quote attempt:', err)
        }
      }
      
      if (onLoadComplete) {
        await onLoadComplete()
      }
    } catch (err) {
      console.error(err)
      // Si le devis n'existe pas (404), rediriger vers la liste
      const errorMessage = err.message?.toLowerCase() || ''
      if (errorMessage.includes('non trouvé') || 
          errorMessage.includes('not found') || 
          errorMessage.includes('404') ||
          errorMessage.includes('devis non trouvé')) {
        router.push('/')
        return
      }
      throw err
    }
  }

  // Assign commercial
  const { assignCommercial } = useQuoteDetailAssign(
    quoteId,
    quote,
    availableCommercials,
    currentUser,
    loadQuote
  )

  const updateStatus = async (status) => {
    try {
      await apiPatch(`/api/quotes/${quoteId}/status`, { status }, {
        successMessage: 'Statut mis à jour avec succès',
        showSuccessToast: true,
        errorMessage: 'Erreur lors de la mise à jour du statut'
      })
      quote.value.status = status
    } catch (err) {
      console.error('Erreur détaillée:', err)
      throw err
    }
  }

  const deleteQuote = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return false
    }
    try {
      await apiDelete(`/api/quotes/${quoteId}`, {
        successMessage: 'Demande supprimée avec succès',
        showSuccessToast: true,
        errorMessage: 'Erreur lors de la suppression'
      })
      return true
    } catch (err) {
      console.error(err)
      return false
    }
  }


  return {
    quote,
    attempt,
    loading,
    isEditing,
    editedQuote,
    saving,
    commercials,
    canEdit,
    canGenerateQuote,
    canChangeStatus,
    availableCommercials,
    getCommercialsForSelect: computed(() => getCommercialsForSelect(quote.value)),
    loadCommercials,
    loadQuote,
    assignCommercial,
    updateStatus,
    deleteQuote,
    startEditing,
    cancelEditing,
    saveQuote,
    getValue,
    isMissing
  }
}

